import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { Layout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { registerVoterInperson, resetVoterPasswordInperson, getRegisteredVotersStats, toggleOnlineRegistration } from "../../lib/api";
import { UserPlus, Mail, CheckCircle2, AlertCircle, Users, UserCheck, Clock, KeyRound, Globe, ExternalLink, Copy, Check, QrCode, Upload, FileSpreadsheet, Download, Link2, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { VotingQRCode } from "../../components/VotingQRCode";
import { Switch } from "../../components/ui/switch";
import api from "../../lib/api";

export default function RegisterVotersPage() {
  const { t } = useTranslation();
  const [dni, setDni] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [stats, setStats] = useState(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [pendingResetData, setPendingResetData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [onlineEnabled, setOnlineEnabled] = useState(false);
  const [togglingOnline, setTogglingOnline] = useState(false);
  const [xlsxFile, setXlsxFile] = useState(null);
  const [xlsxUploading, setXlsxUploading] = useState(false);
  const [massFile, setMassFile] = useState(null);
  const [massUploading, setMassUploading] = useState(false);
  const massFileRef = useRef(null);
  const xlsxFileRef = useRef(null);

  // Delete voter states
  const [deleteDni, setDeleteDni] = useState("");
  const [deleteBirthDate, setDeleteBirthDate] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Reset attempts states
  const [resetDni, setResetDni] = useState("");
  const [resetBirthDate, setResetBirthDate] = useState("");
  const [resetLoading2, setResetLoading2] = useState(false);

  const entityId = localStorage.getItem("entity_id");
  const onlineRegUrl = `${window.location.origin}/voter/register-online/${entityId}`;

  useEffect(() => {
    loadStats();
    loadOnlineStatus();
  }, []);

  const loadOnlineStatus = async () => {
    try {
      const res = await api.get(`/public/entity-registration-info/${entityId}`);
      setOnlineEnabled(res.data.online_registration_enabled || false);
    } catch {
      // ignore
    }
  };

  const handleToggleOnline = async () => {
    setTogglingOnline(true);
    try {
      const res = await toggleOnlineRegistration();
      setOnlineEnabled(res.data.online_registration_enabled);
      toast.success(res.data.online_registration_enabled ? t('registerVoters.onlineRegEnabled') : t('registerVoters.onlineRegDisabled'));
    } catch {
      toast.error(t('errors.generic'));
    } finally {
      setTogglingOnline(false);
    }
  };

  const _uploadFile = (url, formData) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url, true);
      xhr.responseType = "blob";
      xhr.withCredentials = true;
      xhr.setRequestHeader("Accept-Language", i18n.language || "ca");
      xhr.onload = () => {
        resolve({
          status: xhr.status,
          data: xhr.response,
          headers: {
            get: (name) => xhr.getResponseHeader(name),
          },
        });
      };
      xhr.onerror = () => reject(new Error("Network error"));
      xhr.send(formData);
    });
  };

  const handleXlsxUpload = async () => {
    if (!xlsxFile) return;
    setXlsxUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", xlsxFile);
      const baseUrl = `${window.location.origin}/api`;
      const res = await _uploadFile(`${baseUrl}/entities/check-registration-xlsx`, formData);
      if (res.status >= 400) {
        let msg = t('errors.processingFile');
        try {
          const text = await res.data.text();
          const json = JSON.parse(text);
          msg = json.detail || msg;
        } catch (_) { /* ignore parse errors */ }
        toast.error(msg);
        return;
      }
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      const disposition = res.headers.get('content-disposition') || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      link.setAttribute("download", filenameMatch ? filenameMatch[1] : t('registerVoters.checkStatusFilename'));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('results.fileDownloaded'));
      setXlsxFile(null);
    } catch (err) {
      toast.error(t('errors.processingFile'));
    } finally {
      setXlsxUploading(false);
    }
  };

  const handleMassRegister = async () => {
    if (!massFile) return;
    setMassUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", massFile);
      const baseUrl = `${window.location.origin}/api`;
      const res = await _uploadFile(`${baseUrl}/entities/mass-register-xlsx`, formData);
      if (res.status >= 400) {
        let msg = t('errors.processingFile');
        try {
          const text = await res.data.text();
          const json = JSON.parse(text);
          msg = json.detail || msg;
        } catch (_) { /* ignore parse errors */ }
        toast.error(msg);
        return;
      }
      // Check if it's JSON (all registered) or blob (xlsx)
      const contentType = res.data.type || '';
      if (contentType.includes('application/json')) {
        const text = await res.data.text();
        const json = JSON.parse(text);
        toast.info(json.message || t('registerVoters.allAlreadyRegistered'));
      } else {
        const url = window.URL.createObjectURL(res.data);
        const link = document.createElement("a");
        link.href = url;
        const disposition = res.headers.get('content-disposition') || '';
        const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
        link.setAttribute("download", filenameMatch ? filenameMatch[1] : t('registerVoters.massRegisterFilename'));
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success(t('results.fileDownloaded'));
      }
      setMassFile(null);
    } catch (err) {
      toast.error(t('errors.processingFile'));
    } finally {
      setMassUploading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await getRegisteredVotersStats();
      setStats(res.data);
    } catch {
      // ignore
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!dni.trim() || !birthDate.trim()) {
      toast.error(t('registerVoters.dniAndBirthRequired'));
      return;
    }

    setLoading(true);
    setSuccess(null);
    setLinkCopied(false);
    try {
      const res = await registerVoterInperson(dni.trim(), birthDate.trim());
      setSuccess(res.data);
      toast.success(t('registerVoters.registered'));
      setDni("");
      setBirthDate("");
      loadStats();
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.detail === "already_registered") {
        setPendingResetData({ dni: dni.trim(), birthDate: birthDate.trim() });
        setShowResetDialog(true);
      } else {
        const msg = err.response?.data?.detail || t('errors.registerVoter');
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async () => {
    if (!pendingResetData) return;
    setResetLoading(true);
    setLinkCopied(false);
    try {
      const res = await resetVoterPasswordInperson(
        pendingResetData.dni,
        pendingResetData.birthDate
      );
      toast.success(t('registerVoters.passwordChangeLinkGenerated'));
      setSuccess({ message: res.data.message, link: res.data.link, is_reset: true });
      setDni("");
      setBirthDate("");
    } catch (err) {
      const msg = err.response?.data?.detail || t('errors.resetLinkError');
      toast.error(msg);
    } finally {
      setResetLoading(true);
      setShowResetDialog(false);
      setPendingResetData(null);
      setResetLoading(false);
    }
  };

  const handleCancelReset = () => {
    setShowResetDialog(false);
    setPendingResetData(null);
  };

  const handleDeleteVoter = async () => {
    setDeleteLoading(true);
    try {
      await api.delete("/census/global/voter", {
        data: { dni: deleteDni.trim(), birth_date: deleteBirthDate.trim() }
      });
      toast.success(t('registerVoters.voterDeletedSuccess'));
      setDeleteDni("");
      setDeleteBirthDate("");
      setShowDeleteConfirm(false);
      loadStats();
    } catch (err) {
      const msg = err.response?.data?.detail || t('errors.generic');
      toast.error(msg);
      setShowDeleteConfirm(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="register-voters-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('registerVoters.title')}</h1>
          <p className="text-slate-500 mt-1">{t('registerVoters.subtitle')}</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t('registerVoters.totalVoters')}</p>
                    <p className="text-2xl font-bold text-slate-900" data-testid="stats-total">{stats.total}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <UserCheck className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t('registerVoters.withPassword')}</p>
                    <p className="text-2xl font-bold text-green-600" data-testid="stats-registered">{stats.registered}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Clock className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t('registerVoters.withoutPassword')}</p>
                    <p className="text-2xl font-bold text-amber-600" data-testid="stats-pending">{stats.pending}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* 1. Registration Form (In-person) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-blue-600" />
              {t('registerVoters.inPersonTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="dni">DNI / NIF</Label>
                  <Input
                    id="dni"
                    data-testid="register-dni-input"
                    placeholder="12345678A"
                    value={dni}
                    onChange={(e) => setDni(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="birthDate">{t('results.birthDate')}</Label>
                  <Input
                    id="birthDate"
                    data-testid="register-birthdate-input"
                    type="date"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="register-submit-btn"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      {t('common.processing')}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Link2 className="h-4 w-4" />
                      {t('registerVoters.registerAndGetLink')}
                    </span>
                  )}
                </Button>
              </div>
            </form>

            {/* Success message with link */}
            {success && (
              <div
                className={`mt-4 p-4 border rounded-lg ${
                  success.is_reset
                    ? "bg-amber-50 border-amber-200"
                    : "bg-green-50 border-green-200"
                }`}
                data-testid="register-success-msg"
              >
                <div className="flex items-start gap-3">
                  {success.is_reset ? (
                    <KeyRound className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <p className={`font-medium ${success.is_reset ? "text-amber-800" : "text-green-800"}`}>
                      {success.message}
                    </p>
                    {!success.is_reset && success.is_new_voter && (
                      <p className="text-sm text-green-600 mt-1">
                        {t('registerVoters.newCensusRecord')}
                      </p>
                    )}
                  </div>
                </div>
                {success.link && (
                  <div className="mt-3 p-3 bg-white rounded-lg border border-slate-200">
                    <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1">
                      <Link2 className="h-3 w-3" /> {t('registerVoters.linkForVoter')}
                    </p>
                    <div className="flex gap-2">
                      <div className="flex-1 p-2 bg-slate-50 rounded border border-slate-200 font-mono text-xs break-all select-all" data-testid="inperson-reg-link">
                        {success.link}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid="copy-inperson-link-btn"
                        onClick={() => {
                          navigator.clipboard.writeText(success.link);
                          setLinkCopied(true);
                          setTimeout(() => setLinkCopied(false), 2000);
                          toast.success(t('results.linkCopied'));
                        }}
                      >
                        {linkCopied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      {t('registerVoters.linkExpires48h')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Info box */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">{t('registerVoters.howItWorks')}</p>
                <ol className="list-decimal list-inside space-y-1 text-blue-700">
                  <li>{t('registerVoters.step1')}</li>
                  <li>{t('registerVoters.step2')}</li>
                  <li>{t('registerVoters.step3')}</li>
                  <li>{t('registerVoters.step4')}</li>
                  <li>{t('registerVoters.step5')}</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 1b. Reset Identification Attempts */}
        <Card className="border-amber-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-amber-600" />
              {t('registerVoters.resetAttemptsTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-4">
              {t('registerVoters.resetAttemptsDesc')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="resetDni">DNI / NIF</Label>
                <Input
                  id="resetDni"
                  data-testid="reset-attempts-dni-input"
                  placeholder="12345678A"
                  value={resetDni}
                  onChange={(e) => setResetDni(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="resetBirthDate">{t('results.birthDate')}</Label>
                <Input
                  id="resetBirthDate"
                  data-testid="reset-attempts-birthdate-input"
                  type="date"
                  value={resetBirthDate}
                  onChange={(e) => setResetBirthDate(e.target.value)}
                />
              </div>
            </div>
            <div className="mt-4">
              <Button
                type="button"
                disabled={!resetDni.trim() || !resetBirthDate.trim() || resetLoading2}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="reset-attempts-btn"
                onClick={async () => {
                  setResetLoading2(true);
                  try {
                    const res = await api.post("/entities/reset-voter-attempts", {
                      dni: resetDni.trim(),
                      birth_date: resetBirthDate.trim(),
                    });
                    toast.success(res.data.message || t('registerVoters.resetAttemptsSuccess'));
                    setResetDni("");
                    setResetBirthDate("");
                  } catch (err) {
                    const msg = err.response?.data?.detail || t('errors.generic');
                    toast.error(msg);
                  } finally {
                    setResetLoading2(false);
                  }
                }}
              >
                {resetLoading2 ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    {t('common.processing')}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <RotateCcw className="h-4 w-4" />
                    {t('registerVoters.resetAttemptsBtn')}
                  </span>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* 2. Mass Registration via XLSX */}
        <Card className="border-purple-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-purple-600" />
              {t('registerVoters.massRegTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-4">
              {t('registerVoters.massRegDesc')}
            </p>
            <div className="mb-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const res = await api.get("/entities/voters-template", { responseType: "blob" });
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement("a"); a.href = url;
                    a.download = res.headers["content-disposition"]?.split("filename=")[1]?.replace(/"/g, "") || "plantilla_votants.xlsx";
                    a.click(); window.URL.revokeObjectURL(url);
                  } catch { toast.error(t('common.error')); }
                }}
                data-testid="download-voters-template-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                {t('registerVoters.downloadTemplate')}
              </Button>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept=".xlsx"
                ref={massFileRef}
                data-testid="mass-register-upload-input"
                onChange={(e) => setMassFile(e.target.files?.[0] || null)}
                className="hidden"
              />
              <div className="flex-1 flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => massFileRef.current?.click()}
                  className="shrink-0"
                  data-testid="mass-register-choose-file-btn"
                >
                  {t('common.chooseFile')}
                </Button>
                <span className="text-sm text-slate-500 truncate">
                  {massFile ? massFile.name : t('common.noFileSelected')}
                </span>
              </div>
              <Button
                onClick={handleMassRegister}
                disabled={!massFile || massUploading}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="mass-register-btn"
              >
                {massUploading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    {t('common.processing')}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    {t('registerVoters.generateAndDownload')}
                  </span>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* 3. Online Registration Link + QR */}
        {entityId && (
          <Card className={onlineEnabled ? "border-blue-200 bg-blue-50" : "border-slate-200"}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Globe className={`h-5 w-5 ${onlineEnabled ? "text-blue-600" : "text-slate-400"}`} />
                  {t('registerVoters.onlineRegTitle')}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium ${onlineEnabled ? "text-green-600" : "text-slate-400"}`}>
                    {onlineEnabled ? t('common.enabled') : t('common.disabled')}
                  </span>
                  <Switch
                    checked={onlineEnabled}
                    onCheckedChange={handleToggleOnline}
                    disabled={togglingOnline}
                    data-testid="online-reg-toggle"
                  />
                </div>
              </div>
            </CardHeader>
            {onlineEnabled && (
            <CardContent>
              <p className="text-sm text-blue-800 mb-4">
                {t('registerVoters.onlineRegShareDesc')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs font-medium text-blue-800 mb-2 flex items-center gap-1">
                    <ExternalLink className="h-3 w-3" /> {t('consultaDetail.webLink')}
                  </p>
                  <div className="flex gap-2">
                    <div className="flex-1 p-3 bg-white rounded-lg border border-blue-200 font-mono text-sm truncate">
                      {onlineRegUrl}
                    </div>
                    <Button
                      variant="outline"
                      className="border-blue-200 text-blue-700 hover:bg-blue-100"
                      data-testid="copy-online-reg-url-btn"
                      onClick={() => {
                        navigator.clipboard.writeText(onlineRegUrl);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                        toast.success(t('results.linkCopied'));
                      }}
                    >
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                    <a href={onlineRegUrl} target="_blank" rel="noreferrer">
                      <Button
                        variant="outline"
                        className="border-blue-200 text-blue-700 hover:bg-blue-100"
                        data-testid="open-online-reg-btn"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </a>
                  </div>
                </div>
                <div className="flex flex-col items-center">
                  <p className="text-xs font-medium text-blue-800 mb-2 flex items-center gap-1">
                    <QrCode className="h-3 w-3" /> {t('consultaDetail.qrCode')}
                  </p>
                  <VotingQRCode
                    url={onlineRegUrl}
                    title={t('registerVoters.onlineRegQRTitle')}
                    size={160}
                  />
                </div>
              </div>
            </CardContent>
            )}
          </Card>
        )}

        {/* 4. Check Registration Status from XLSX */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-green-600" />
              {t('registerVoters.checkStatusTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-4">
              {t('registerVoters.checkStatusDesc')}
            </p>
            <div className="mb-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const res = await api.get("/entities/check-status-template", { responseType: "blob" });
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement("a"); a.href = url;
                    a.download = res.headers["content-disposition"]?.split("filename=")[1]?.replace(/"/g, "") || "plantilla_comprovacio.xlsx";
                    a.click(); window.URL.revokeObjectURL(url);
                  } catch { toast.error(t('common.error')); }
                }}
                data-testid="download-check-template-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                {t('registerVoters.downloadTemplate')}
              </Button>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept=".xlsx"
                ref={xlsxFileRef}
                data-testid="xlsx-upload-input"
                onChange={(e) => setXlsxFile(e.target.files?.[0] || null)}
                className="hidden"
              />
              <div className="flex-1 flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => xlsxFileRef.current?.click()}
                  className="shrink-0"
                  data-testid="xlsx-choose-file-btn"
                >
                  {t('common.chooseFile')}
                </Button>
                <span className="text-sm text-slate-500 truncate">
                  {xlsxFile ? xlsxFile.name : t('common.noFileSelected')}
                </span>
              </div>
              <Button
                onClick={handleXlsxUpload}
                disabled={!xlsxFile || xlsxUploading}
                className="bg-green-600 hover:bg-green-700"
                data-testid="xlsx-upload-btn"
              >
                {xlsxUploading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    {t('common.processing')}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    {t('registerVoters.checkAndDownload')}
                  </span>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* 5. Delete Voter from Global Census */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              {t('registerVoters.deleteVoterTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-4">
              {t('registerVoters.deleteVoterDesc')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="deleteDni">DNI / NIF</Label>
                <Input
                  id="deleteDni"
                  data-testid="delete-voter-dni-input"
                  placeholder="12345678A"
                  value={deleteDni}
                  onChange={(e) => setDeleteDni(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="deleteBirthDate">{t('results.birthDate')}</Label>
                <Input
                  id="deleteBirthDate"
                  data-testid="delete-voter-birthdate-input"
                  type="date"
                  value={deleteBirthDate}
                  onChange={(e) => setDeleteBirthDate(e.target.value)}
                />
              </div>
            </div>
            <div className="mt-4">
              <Button
                type="button"
                variant="destructive"
                disabled={!deleteDni.trim() || !deleteBirthDate.trim() || deleteLoading}
                data-testid="delete-voter-btn"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <span className="flex items-center gap-2">
                  <Trash2 className="h-4 w-4" />
                  {t('registerVoters.deleteVoterBtn')}
                </span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Delete Voter Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(false)}>
        <DialogContent data-testid="delete-voter-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              {t('registerVoters.deleteVoterConfirmTitle')}
            </DialogTitle>
            <DialogDescription className="text-left pt-2">
              {t('registerVoters.deleteVoterConfirmDesc')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={deleteLoading}
              data-testid="delete-voter-cancel-btn"
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteVoter}
              disabled={deleteLoading}
              data-testid="delete-voter-confirm-btn"
            >
              {deleteLoading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  {t('common.processing')}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Trash2 className="h-4 w-4" />
                  {t('registerVoters.deleteVoterBtn')}
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showResetDialog} onOpenChange={handleCancelReset}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-amber-600" />
              {t('registerVoters.alreadyRegisteredTitle')}
            </DialogTitle>
            <DialogDescription className="text-left pt-2">
              {t('registerVoters.alreadyRegisteredDesc')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={handleCancelReset}
              disabled={resetLoading}
              data-testid="reset-dialog-cancel-btn"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleConfirmReset}
              disabled={resetLoading}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="reset-dialog-confirm-btn"
            >
              {resetLoading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  {t('common.processing')}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Link2 className="h-4 w-4" />
                  {t('registerVoters.generateChangeLink')}
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
